/**
 * UserCentricsService
 *
 * Centralized service for checking user consent for various services.
 * In Development/Testing environments, always returns true (bypass consent).
 * In Production/Staging, delegates to Usercentrics API.
 */
export class UserCentricsService {
    
    constructor() {
        this.environment = null;
        this.listeners = new Map(); // service -> Set of callbacks
        this.consentCache = new Map(); // service -> boolean
        this.isReady = false;

        this.init();
    }

    init() {
        // Detect environment from body data attribute
        this.environment = document.body.getAttribute('data-environment');

        // In development/testing, bypass all consent checks
        if (this.isDevelopmentEnvironment()) {
            this.isReady = true;
            console.log('[UserCentricsService] Development environment detected - bypassing consent checks');
            return;
        }

        // In production/staging, set up Usercentrics integration
        this.setupUsercentricsListeners();
        this.checkUsercentricsAvailability();
    }

    /**
     * @returns {boolean}
     */
    isDevelopmentEnvironment() {
        return this.environment === 'Development' || this.environment === 'Testing';
    }

    checkUsercentricsAvailability() {
        const checkUC = () => {
            if (document.readyState === 'complete' && window.uc && window.uc.ucapi) {
                this.isReady = true;
                console.log('[UserCentricsService] Usercentrics API ready');
                this.loadInitialConsentState();
            } else {
                setTimeout(checkUC, 1000);
            }
        };

        setTimeout(checkUC, 1000);
    }

    loadInitialConsentState() {
        if (!window.uc || !window.uc.ucapi) return;

        const allServices = window.uc.ucapi.getAllServicesInfo();
        const whitelistedServices = window.uc.ucapi.getWhitelistedServices();

        whitelistedServices.forEach((serviceKey) => {
            const serviceName = allServices[serviceKey];
            this.consentCache.set(serviceName, true);
        });
    }

    setupUsercentricsListeners() {
        window.addEventListener('ucEvent', (e) => {
            if (!e.detail) return;

            const isConsentEvent = e.detail.event === 'consent_status' ||
                                   e.detail.action === 'onInitialPageLoad';

            if (isConsentEvent) {
                // Update cache and notify listeners
                Object.keys(e.detail).forEach((serviceName) => {
                    if (serviceName === 'event' || serviceName === 'action') return;

                    const hasConsent = e.detail[serviceName] === true;
                    const previousConsent = this.consentCache.get(serviceName);

                    this.consentCache.set(serviceName, hasConsent);

                    // Notify listeners if consent changed
                    if (previousConsent !== hasConsent) {
                        this.notifyListeners(serviceName, hasConsent);
                    }
                });
            }
        });
    }

    /**
     * @param {string} serviceName - Name of the service (e.g., "Google Maps", "BD Advanced Search")
     * @returns {boolean}
     */
    isServiceEnabled(serviceName) {
        // In development/testing, always return true
        if (this.isDevelopmentEnvironment()) {
            return true;
        }

        // In production/staging, check Usercentrics
        if (!this.isReady || !window.uc || !window.uc.ucapi) {
            console.warn(`[UserCentricsService] Usercentrics not ready, returning false for ${serviceName}`);
            return false;
        }

        // Check cache first
        if (this.consentCache.has(serviceName)) {
            return this.consentCache.get(serviceName);
        }

        // Fallback: check Usercentrics API directly
        const allServices = window.uc.ucapi.getAllServicesInfo();
        const whitelistedServices = window.uc.ucapi.getWhitelistedServices();

        for (const serviceKey of whitelistedServices) {
            if (allServices[serviceKey] === serviceName) {
                this.consentCache.set(serviceName, true);
                return true;
            }
        }

        this.consentCache.set(serviceName, false);
        return false;
    }

    /**
     * Register a callback for consent changes
     * @param {string} serviceName - Service to watch
     * @param {Function} callback - Callback function (receives boolean consent status)
     * @returns {Function} Unsubscribe function
     */
    onConsentChange(serviceName, callback) {
        if (!this.listeners.has(serviceName)) {
            this.listeners.set(serviceName, new Set());
        }

        this.listeners.get(serviceName).add(callback);

        // Return unsubscribe function
        return () => {
            const serviceListeners = this.listeners.get(serviceName);
            if (serviceListeners) {
                serviceListeners.delete(callback);
            }
        };
    }

    /**
     * @param {string} serviceName
     * @param {boolean} hasConsent
     */
    notifyListeners(serviceName, hasConsent) {
        const serviceListeners = this.listeners.get(serviceName);
        if (serviceListeners) {
            serviceListeners.forEach(callback => {
                try {
                    callback(hasConsent);
                } catch (error) {
                    console.error(`[UserCentricsService] Error in consent change callback for ${serviceName}:`, error);
                }
            });
        }
    }
}
